import React, { useCallback, useMemo } from 'react';
import { useFormStore } from 'simple-creator/stores/formStore';
import { useFormContext } from 'simple-creator/contexts/FormContext';
import { CategoryBadge, TagBadge, useBalloonEventsMetadata, useUser } from 'react-ui';
import { CategoryModel, Language } from 'ts-core';
import type { Tag } from 'ts-core';
import { getPendingFileMetadata } from 'simple-creator/services/imageService';
import { extractYouTubeId, isInstagramUrl } from 'simple-creator/utils/imageUtils';
import { SiInstagram } from 'react-icons/si';

/** Detect whether a media URL points to a video file. */
function isVideoMedia(url: string): boolean {
  if (url.startsWith('blob:')) {
    const meta = getPendingFileMetadata(url);
    return !!meta?.isVideo;
  }
  if (url.startsWith('data:video/')) return true;
  return /\.(mp4|webm|mov)(\?|$)/i.test(url);
}

const DESCRIPTION_TYPE_LABELS: Record<string, string> = {
  'print-text': 'Print Text',
  teaser: 'Teaser',
  'venue-textual-info': 'Venue Info',
  zusatzinfo: 'Additional Info',
  'price-info': 'Price Info',
  'legal-contact': 'Legal Contact',
  venues: 'Venues',
  subtitle: 'Subtitle',
  main: 'Description',
};

function getDescriptionLabel(type: string): string {
  return DESCRIPTION_TYPE_LABELS[type] ?? type.replace(/-/g, ' ');
}

export const ReviewStep: React.FC = () => {
  const { formData } = useFormStore();
  const { mode, config } = useFormContext();
  const { metadata } = useBalloonEventsMetadata();
  const { user } = useUser();
  const canViewSystemTags = user?.hasPermission('view-tags-index') ?? false;

  const language = useMemo(() => {
    return config?.locale === 'de' ? Language.de : Language.en;
  }, [config?.locale]);

  const findCategoryBySlug = useCallback((slug: string): CategoryModel | null => {
    if (!metadata || !slug) return null;

    const allCategories = (metadata.getAllCategories() || []) as CategoryModel[];
    const normalizedSlug = slug.toLowerCase();
    return allCategories.find((c) => c.slug?.toLowerCase() === normalizedSlug) || null;
  }, [metadata]);

  const metadataTagsBySlug = useMemo(() => {
    const tags: Tag[] = metadata?.tags ?? [];
    return new Map(tags.map((tag: Tag) => [tag.slug, tag]));
  }, [metadata]);

  const systemTagSlugs = useMemo(() => {
    const tags: Tag[] = metadata?.tags ?? [];
    return new Set(tags.filter((tag: Tag) => tag.type === 'system').map((tag: Tag) => tag.slug));
  }, [metadata]);

  const visibleTags = useMemo(() => {
    const fallbackTag = (slug: string): Tag => ({
      id: slug,
      name: slug,
      slug,
      type: 'user',
    });

    const selectedTags = (formData.tags ?? []).map((slug) => metadataTagsBySlug.get(slug) ?? fallbackTag(slug));
    return selectedTags.filter((tag) => canViewSystemTags || !systemTagSlugs.has(tag.slug));
  }, [formData.tags, metadataTagsBySlug, canViewSystemTags, systemTagSlugs]);

  // Get placeholder image URL based on category
  const getPlaceholderImageUrl = useMemo(() => {
    const base = "https://be-res.s3.eu-central-1.amazonaws.com/images/placeholder";
    if (formData.categories.length > 0) {
      // Use first category slug for placeholder
      return `${base}/${encodeURIComponent(formData.categories[0])}.webp`;
    }
    return `${base}/other.webp`;
  }, [formData.categories]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="h3">Almost done!</h3>
        <p className="text-gray-600">
          Review your event details before {mode === 'edit' ? 'updating' : 'publishing'}
        </p>
      </div>

      {/* Event Preview Card */}
      <div className="preview-card">
        {/* Event Image */}
        {formData.images && formData.images.length > 0 ? (
          <div className="relative h-48 overflow-hidden">
            {isVideoMedia(formData.images[0].url) ? (
              <video
                src={formData.images[0].url}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
            ) : (
              <img
                src={formData.images[0].url}
                alt={formData.title}
                className="w-full h-full object-cover"
              />
            )}
            {formData.images.length > 1 && (
              <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
                +{formData.images.length - 1} more
              </div>
            )}
            {formData.images[0].copyright && (
              <div className="absolute bottom-2 left-2 max-w-[60%] truncate rounded bg-black/50 text-white text-[11px] px-2 py-0.5">
                © {formData.images[0].copyright}
              </div>
            )}
          </div>
        ) : (
          <div className="relative h-48 overflow-hidden">
            <img
              src={getPlaceholderImageUrl}
              alt="Event placeholder"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to balloon logo if category placeholder doesn't exist
                e.currentTarget.src = "https://res.balloon-events.com/logos/colored-notext.png";
                e.currentTarget.className = "w-24 h-24 object-contain mx-auto mt-12";
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 pointer-events-none" />
          </div>
        )}

        <div className="p-6">
          {/* Title & Subtitle */}
          <h3 className="h3 mb-2">{formData.title}</h3>
          {formData.subtitle && (
            <p className="text-gray-600 mb-4">{formData.subtitle}</p>
          )}

          {/* Categories */}
          {formData.categories.length > 0 && (
            <div className="flex gap-2 mb-4">
              {formData.categories.map((slug) => {
                const category = findCategoryBySlug(slug);
                if (!category) return null;

                return (
                  <span key={slug} className="text-sm">
                    <CategoryBadge
                      category={category}
                      language={language}
                    />
                  </span>
                );
              })}
            </div>
          )}

          {/* Date & Time */}
          {formData.dates.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 text-gray-700">
                <span>📅</span>
                <div>
                  {formData.dates.map((date, index) => (
                    <div key={index}>
                      {new Date(date.start).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })} at {new Date(date.start).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}{date.end && (<>
                        {' – '}
                        {new Date(date.start).toDateString() !== new Date(date.end).toDateString() && (
                          `${new Date(date.end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} `
                        )}
                        {new Date(date.end).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </>)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Location */}
          {formData.location.address && (
            <div className="mb-4">
              <div className="flex items-start gap-2 text-gray-700">
                <span>📍</span>
                <div>
                  {formData.location.name && (
                    <div className="font-medium">{formData.location.name}</div>
                  )}
                  <div>{formData.location.address}</div>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          {formData.description && (
            <div className="mb-4">
              <div 
                className="text-gray-700 line-clamp-3 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: formData.description }}
              />
            </div>
          )}

          {/* Descriptions (structured) */}
          {formData.descriptions && formData.descriptions.length > 0 && (
            <div className="mb-4 space-y-2">
              {formData.descriptions.map((desc, index) => (
                <div key={index} className="text-sm">
                  <span className="text-xs font-medium text-gray-500">{getDescriptionLabel(desc.type)}:</span>
                  <div 
                    className="text-gray-700 line-clamp-2 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: desc.content }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Contact Info */}
          {formData.contactInfos && formData.contactInfos.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <p className="label mb-2">Contact Information</p>
              <div className="space-y-1.5">
                {formData.contactInfos.map((contact, index) => {
                  const icons: Record<string, string> = {
                    email: '✉️',
                    phone: '📞',
                    website: '🌐',
                    name: '👤',
                    landline: '☎️',
                    ticket: '🎫',
                    'ticket-print-info': '🎟️',
                  };
                  const labels: Record<string, string> = {
                    email: 'Email',
                    phone: 'Phone',
                    website: 'Website',
                    name: 'Contact Person',
                    landline: 'Landline',
                    ticket: 'Tickets',
                    'ticket-print-info': 'Ticket Info',
                  };
                  return (
                    <div key={index} className="text-sm text-gray-600 flex items-start gap-2">
                      <span>{icons[contact.type] || '📋'}</span>
                      <div>
                        <span className="text-xs font-medium text-gray-500">{labels[contact.type] || contact.type}:</span>
                        {contact.type === 'website' || contact.type === 'ticket' ? (
                          <a href={contact.content} target="_blank" rel="noopener noreferrer" className="text-balloon hover:underline block">
                            {contact.content}
                          </a>
                        ) : (
                          <span className="block">{contact.content}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tags */}
          {visibleTags.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <p className="label mb-2">Tags</p>
              <div className="flex gap-2 flex-wrap">
                {visibleTags.map((tag) => (
                  <TagBadge
                    key={tag.slug}
                    tag={{ slug: tag.slug, name: tag.name }}
                    isSystem={systemTagSlugs.has(tag.slug)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Organizer */}
          {formData.organisers && formData.organisers.length > 0 && (formData.organisers[0].name || formData.organisers[0].salutation) && (
            <div className="border-t pt-4 mt-4">
              <p className="label mb-2">Organizer</p>
              <div className="space-y-3">
                {/* Name & Salutation */}
                <div className="flex items-center gap-2 text-gray-700">
                  <span>🏢</span>
                  <span className="font-medium">
                    {formData.organisers[0].salutation && `${formData.organisers[0].salutation} `}
                    {formData.organisers[0].name}
                  </span>
                </div>

                {/* Organizer Location */}
                {formData.organisers[0].location && formData.organisers[0].location.length > 0 && (
                  <div className="ml-6 text-sm text-gray-600">
                    <div className="flex items-start gap-2">
                      <span>📍</span>
                      <div>
                        {formData.organisers[0].location[0].name && (
                          <div className="font-medium">{formData.organisers[0].location[0].name}</div>
                        )}
                        {formData.organisers[0].location[0].address && (
                          <div>
                            {formData.organisers[0].location[0].address.street && (
                              <div>{formData.organisers[0].location[0].address.street}</div>
                            )}
                            {(formData.organisers[0].location[0].address.zip || formData.organisers[0].location[0].address.locality) && (
                              <div>
                                {formData.organisers[0].location[0].address.zip} {formData.organisers[0].location[0].address.locality}
                              </div>
                            )}
                            {(formData.organisers[0].location[0].address.region || formData.organisers[0].location[0].address.country) && (
                              <div>
                                {formData.organisers[0].location[0].address.region}{formData.organisers[0].location[0].address.region && formData.organisers[0].location[0].address.country && ', '}{formData.organisers[0].location[0].address.country}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Organizer Contact Info */}
                {formData.organisers[0].contactInfos && formData.organisers[0].contactInfos.length > 0 && (
                  <div className="ml-6 text-sm text-gray-600 space-y-1">
                    {formData.organisers[0].contactInfos.map((contact, index) => {
                      const icons: Record<string, string> = {
                        email: '✉️',
                        phone: '📞',
                        website: '🌐',
                      };
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <span>{icons[contact.type] || '📋'}</span>
                          {contact.type === 'website' ? (
                            <a href={contact.content} target="_blank" rel="noopener noreferrer" className="text-balloon hover:underline">
                              {contact.content}
                            </a>
                          ) : (
                            <span>{contact.content}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All Images Preview */}
      {formData.images && formData.images.length > 1 && (
        <div className="preview-card p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Event Images ({formData.images.length})
          </h4>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {formData.images.map((image, index) => (
              <div key={index} className="relative aspect-square rounded overflow-hidden">
                {isVideoMedia(image.url) ? (
                  <video
                    src={image.url}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={image.url}
                    alt={`Event image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                {index === 0 && (
                  <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-balloon text-white text-xs rounded">
                    Main
                  </div>
                )}
                {image.copyright && (
                  <div className="absolute bottom-1 left-1 max-w-[80%] truncate rounded bg-black/50 text-white text-[10px] px-1.5 py-0.5">
                    © {image.copyright}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video & reel links preview (YouTube + Instagram) */}
      {formData.youtubeLinks && formData.youtubeLinks.length > 0 && (
        <div className="preview-card p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Videos &amp; reels ({formData.youtubeLinks.length})
          </h4>
          <ul className="space-y-2">
            {formData.youtubeLinks.map((url, index) => {
              const videoId = extractYouTubeId(url);
              return (
                <li key={index} className="flex items-center gap-3">
                  {videoId ? (
                    <img
                      src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                      alt="YouTube thumbnail"
                      className="w-20 h-12 rounded object-cover flex-shrink-0 bg-gray-200"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : isInstagramUrl(url) ? (
                    <div className="w-20 h-12 rounded flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white">
                      <SiInstagram className="w-5 h-5" aria-hidden />
                    </div>
                  ) : null}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={url}
                    className="flex-1 min-w-0 text-sm text-balloon hover:underline truncate"
                  >
                    {url}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Auto-save indicator */}
      <div className="text-center text-sm text-gray-500">
        ✓ Draft auto-saved
      </div>
    </div>
  );
};
